import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, ExternalLink, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { getScheduleReviews } from "../../lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function ScheduleReviewsTab() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getScheduleReviews();
      setRows(res.data || []);
    } catch {
      setError(true);
      toast.error("Failed to load schedule reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <Card data-testid="schedule-reviews-report">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Schedule Reviews
            </CardTitle>
            <CardDescription>
              Students whose schedule is currently flagged for review. Open a file to review and clear the flag.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-schedule-reviews-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 rounded-md bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-10 text-sm text-slate-500">
            Could not load schedule reviews.{" "}
            <button className="text-blue-600 underline" onClick={load}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500" data-testid="schedule-reviews-empty">
            No students are flagged for schedule review right now.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3 font-medium">Student</th>
                  <th className="py-2 pr-3 font-medium">Grade</th>
                  <th className="py-2 pr-3 font-medium">Reason</th>
                  <th className="py-2 pr-3 font-medium">Flagged by</th>
                  <th className="py-2 pr-3 font-medium">Review date</th>
                  <th className="py-2 pr-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.student_id}
                    className="border-b last:border-0 hover:bg-amber-50/40"
                    data-testid={`schedule-review-row-${r.student_id}`}
                  >
                    <td className="py-2.5 pr-3 font-medium text-slate-800">{r.display_name}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{r.grade || "—"}</td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {r.note ? r.note : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{r.flagged_by_name || "—"}</td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <CalendarClock className="h-3.5 w-3.5" />
                        {fmtDate(r.review_date || r.flagged_at)}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`open-student-${r.student_id}`}
                        onClick={() => navigate(`/students/${r.student_id}?tab=schedule`)}
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                        Open file
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
